import {Stack, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';
import InputActionButtons from './InputActionButtons';
import {useGetCompany} from 'hooks/swr';
import {useInputMenuContext} from 'context/InputMenu';
import {type Dispatch, type SetStateAction, useEffect, useState} from 'react';
import {type EditCompany, type Company, type AddCompany} from 'client';
import backendCommunicator from 'services/backendCommunicator';
import {type InputMenuAction, inputMenuActionType, SWRKeyEnum} from 'interfaces';
import Alert from 'helpers/Alert';
import useParseURL from 'hooks/useParseURL';
import {mutate} from 'swr';
import {handleKeyDown} from './helpers';
import {CompanyAliasList} from './Company/CompanyAliasList';

export const CompanyMenu = () => {
  const {t} = useTranslation('common');
  const {planId} = useParseURL();
  const inputMenuContext = useInputMenuContext();
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const inputMenuState = inputMenuContext?.state;
  const {data} = useGetCompany({resourceId: inputMenuState?.formData?.id});
  const [company, setCompany] = useState<Partial<Company>>({planId, isDefault: false, aliases: []});
  const [createUpdateCompany, setCreateUpdateCompany] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    if (data) {
      setCompany(data);
      validateModel(data?.name ?? '');
    }
  }, [data]);

  useEffect(() => {
    if (createUpdateCompany) {
      inputMenuDispatch && createCompany(company, inputMenuDispatch, setCompany, setCreateUpdateCompany);
    }
  }, [createUpdateCompany]);

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  const validateModel = (value: string): void => {
    value !== '' ? setIsValid(true) : setIsValid(false);
  };

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}} onSubmit={handleSubmit}>
      <Stack direction="column" spacing={2} sx={{height: '86vh'}}>
        {/* name <string> */}
        <TextField
          label={t('name')}
          onChange={({target: {value}}) => {
            setCompany({...company, name: value});
            validateModel(value);
          }}
          value={company?.name ?? ''}
          aria-label={t('name')}
          slotProps={{htmlInput: {'data-testid': 'name'}}}
          onKeyDown={(event) => {
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateCompany);
          }}
        />

        <CompanyAliasList
          companyAliases={company.aliases}
          setCompanyAliases={(aliases) => {
            setCompany({...company, aliases});
          }}
        />
      </Stack>

      <InputActionButtons disabled={!isValid} setCreateUpdateInput={setCreateUpdateCompany} />
    </form>
  );
};

const createCompany = (
  company: Partial<Company>,
  inputMenuDispatch: Dispatch<InputMenuAction>,
  setCompany: Dispatch<SetStateAction<Partial<Company>>>,
  setCreateUpdateCompany: Dispatch<SetStateAction<boolean>>,
) => {
  const request = company.id
    ? backendCommunicator.editCompany(company.id, company as EditCompany)
    : backendCommunicator.addCompany(company as AddCompany);

  request
    .then((response) => {
      setCompany(response.data);
      void mutate([company.planId, SWRKeyEnum.GET_PLAN_COMPANIES]);
    })
    .catch((error) => {
      Alert.error(error);
    })
    .finally(() => {
      setCreateUpdateCompany(false);
      inputMenuDispatch({
        type: inputMenuActionType.closeMenu,
      });
    });
};
